import { ReactNode, useEffect, useState } from 'react';

import { DISHWASHER_OPTIONS, ProductType } from '~/core/enums';
import { SectionTypes } from '~/core/enums/section-type-enum';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import {
  ApplianceModel,
  Filter,
  FilterOption,
  ProductDetails,
} from '~/core/models';
import { buildQteAttributes } from '~/core/utils';
import { createTypeSafeContext } from '~/core/utils';
import {
  filterAllEntries,
  getSelectedFiltersMap,
  updateFiltersOptions,
} from '~/core/utils/sortAndFilter';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectBrowsedDishwashers,
  updateBrowsedDishwashers,
  updateInStoreDishwashersUrl,
} from '~/redux/slices/dishwasher';

type DishwasherFilters = Record<string, Filter>;

export interface DishwasherContextValue {
  selectedOption?: DISHWASHER_OPTIONS;
  updateSelectedOption: (newOption?: DISHWASHER_OPTIONS) => void;
  filteredDishwashers: ApplianceModel[];
  isFilterExpanded: boolean;
  updateFilteredDishwashers: (filteredDishwashers: ApplianceModel[]) => void;
  dishwasherFilters: DishwasherFilters;
  updateDishwasherFilters: (dishwasherFilters: DishwasherFilters) => void;
  updateFiltersExpandState: (value: boolean) => void;
  resetDishwashers: () => void;
  dishwashersToProductDetails: () => ProductDetails[];
}

export const [useDishwasherContext, DishwasherContext] =
  createTypeSafeContext<DishwasherContextValue>();

export const DishwasherContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<
    DISHWASHER_OPTIONS | undefined
  >();
  const [filteredDishwashers, setFilteredDishwashers] = useState<
    ApplianceModel[]
  >([]);
  const [isFilterExpanded, setFilterExpanded] = useState(false);
  const [dishwasherFilters, setDishwasherFilters] = useState<DishwasherFilters>(
    {}
  );

  const browsedDishwashers: Record<number, ApplianceModel> = useAppSelector(
    selectBrowsedDishwashers
  );
  const dispatch = useAppDispatch();
  const { getInStoreApplianceUrl } = useRetailerSettings();

  const dishwashersToProductDetails = (): ProductDetails[] => {
    if (!browsedDishwashers) {
      return [];
    }

    return Object.values(browsedDishwashers).map((knownDishwasher) => {
      const detail: ProductDetails = {
        productId: knownDishwasher.productId,
        name: knownDishwasher.name,
        consumerDesc: knownDishwasher.consumerDesc,
        productType: ProductType.DISHWASHER,
        price: knownDishwasher.price,
        priceWithoutDiscount: knownDishwasher.priceWithoutDiscount,
        expirationPromotionDate: knownDishwasher.expirationPromotionDate,
        abbrevDesc: knownDishwasher.abbrevDesc,
        unitOfMeasure: knownDishwasher.unitOfMeasure,
        ...buildQteAttributes(knownDishwasher),
      };

      return detail;
    });
  };

  const updateSelectedOption = (newOption?: DISHWASHER_OPTIONS) => {
    setSelectedOption(newOption);
  };

  const updateDishwasherFilters = (
    dishwasherFilters: Record<string, Filter>
  ) => {
    setDishwasherFilters(dishwasherFilters);
  };

  // expand/collapse the filters
  const updateFiltersExpandState = (value: boolean) => {
    setFilterExpanded(value);
  };

  const resetDishwashers = () => {
    if (selectedOption === DISHWASHER_OPTIONS.BROWSE_DISHWASHERS) {
      dispatch(updateBrowsedDishwashers([]));
      setFilteredDishwashers([]);
    }
    setSelectedOption(undefined);
  };

  const updateFilteredDishwashers = (filteredDishwashers: ApplianceModel[]) => {
    setFilteredDishwashers(filteredDishwashers);
    updateFiltersOptions(dishwasherFilters, Object.values(browsedDishwashers));
  };

  useEffect(() => {
    const url = getInStoreApplianceUrl(SectionTypes.dishwasher);

    url && dispatch(updateInStoreDishwashersUrl(url));
  }, [dispatch, getInStoreApplianceUrl]);

  useEffect(() => {
    const selectedFilters: Record<string, FilterOption[]> =
      getSelectedFiltersMap(dishwasherFilters);

    const dishwasherArray: ApplianceModel[] = Object.values(browsedDishwashers);

    updateFilteredDishwashers(
      filterAllEntries(Object.entries(selectedFilters), dishwasherArray)
    );
  }, [dishwasherFilters, browsedDishwashers]);

  const dishwasherContextValue: DishwasherContextValue = {
    selectedOption,
    updateSelectedOption,
    filteredDishwashers,
    isFilterExpanded,
    updateFilteredDishwashers,
    dishwasherFilters,
    updateDishwasherFilters,
    updateFiltersExpandState,
    resetDishwashers,
    dishwashersToProductDetails,
  };

  return (
    <DishwasherContext.Provider value={dishwasherContextValue}>
      {children}
    </DishwasherContext.Provider>
  );
};
